import useObjectVisibility from '@hooks/useObjectVisibility';
import { useEffect, useMemo, useRef } from 'react';
import IconWrapper from '@components/ui/Icons';
import { eePromoImpHandler, setPromoDetailsCookie } from '@utils/gtm';
import { useLocale } from '@hooks/useLocale';
import PrismicImage from '@components/cms/PrismicImage';

import styles from './LinkCardGrid.module.scss';

export interface LinkCardProps {
  image: {
    url: string;
    alt: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
  link?: string;
  text?: string;
  buttonText?: string;
  trackPromos?: boolean;
  pageType?: string;
  positionWithinSlice: number;
  sliceIndex?: number;
}

const LinkCard = ({
  image,
  link,
  text,
  buttonText,
  trackPromos,
  pageType,
  positionWithinSlice,
  sliceIndex,
}: LinkCardProps) => {
  const locale = useLocale();
  const ref = useRef<any>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      id: buttonText,
      name: buttonText,
      type: 'link',
      url: link,
      position: `${pageType}-${sliceIndex}-LinkCard-${positionWithinSlice}`,
    }),
    [buttonText, link, pageType, positionWithinSlice, sliceIndex]
  );

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);
    }
  }, [isVisible, promoDetails, trackPromos]);

  return (
    <li className={styles.linkCard} key={`link-card-${positionWithinSlice}`}>
      <div className={styles.linkCardInner}>
        <div className={styles.linkCardImage}>
          {link ? (
            <a
              href={`/${locale}${link}`}
              onClick={
                trackPromos
                  ? () =>
                      setPromoDetailsCookie({
                        ...promoDetails,
                        id: image.alt,
                        name: image.alt,
                        type: 'image',
                      })
                  : undefined
              }
            >
              {image?.url && (
                <PrismicImage
                  src={image.url}
                  alt={image?.alt}
                  layout="responsive"
                  quality={85}
                  width={image?.dimensions?.width}
                  height={image?.dimensions?.height}
                />
              )}
            </a>
          ) : (
            image?.url && (
              <PrismicImage
                src={image.url}
                alt={image?.alt}
                layout="responsive"
                quality={85}
                width={image?.dimensions?.width}
                height={image?.dimensions?.height}
              />
            )
          )}
        </div>
        <div className={styles.linkCardContent}>
          <p className={styles.linkCardSubText}>{text}</p>
          <a
            href={`/${locale}${link}`}
            ref={ref}
            className={styles.linkCardButton}
            onClick={
              trackPromos
                ? () => setPromoDetailsCookie(promoDetails)
                : undefined
            }
          >
            {buttonText}
            <IconWrapper
              icon="caret-right"
              height={11}
              classNames={styles.linkCardButtonIcon}
            />
          </a>
        </div>
      </div>
    </li>
  );
};

export default LinkCard;
